import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState, type CSSProperties } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useTimeoutFn } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { Link } from '@sorare/routing';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { SlotMachineAnimatedNumber } from '@sorare/core/src/atoms/animations/SlotMachineAnimatedNumber';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { BrandXL, LabelM } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_MISSIONS_CATEGORY_RARITY,
  ANY_SPORT_PLAY_BOARDS_PRO,
  ANY_SPORT_SERIES_MISSIONS_COLLECT,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useEnableSorare27Nav } from '@sorare/core/src/hooks/useEnableSorare27Nav';
import { isTypes } from '@sorare/core/src/lib/gql';

import { MissionToastBackground } from '#src/components/missions/CurrentUserTaskUpdatedToast/MissionToast/MissionToastBackground/index.tsx';
import { withTargetSo5CompetitionSearchParam } from '#src/hooks/useTargetSo5CompetitionSearchParam.ts';

import type { CollectionToastContent_taskInterface } from './__generated__/index.graphql.ts';

const SlideOutAnimation = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(-100%);
    opacity: 0;
  }
`;
const SlideInAnimation = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const InitialContent = styled(Vertical)`
  transform: translateX(0);
  opacity: 1;

  &.hide {
    animation: ${SlideOutAnimation} 0.6s cubic-bezier(0.4, 0.02, 0.96, 0.56)
      forwards;
  }
`;
const CompleteContent = styled(Horizontal).attrs({ center: true })`
  transform: translateX(-100%);
  opacity: 0;
  position: absolute;
  inset: 0;
  overflow: hidden;

  &.show {
    animation: ${SlideInAnimation} 0.9s var(--ease-out-slide) forwards;
  }
`;
const Icon = styled.img`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  object-fit: contain;
  flex: 0 0 auto;
`;
const ProgressRow = styled(Horizontal).attrs({ gap: 1 })`
  align-items: center;
`;
const ProgressWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;
const StyledBrandXL = styled(BrandXL)`
  text-shadow: 0 0 16px var(--c-nd-500);
`;
const AnimatedLetter = styled.span`
  display: inline-block;
  transform: translateX(calc(var(--offsetFactor) * -1 * 30px));
  &.animate {
    transform: translateX(0);
  }
  transition: transform 0.9s var(--ease-out-slide);
`;
const HiddenOverflow = styled.div`
  position: relative;
  overflow: hidden;
`;

const message = defineMessage({
  defaultMessage: 'Complete!',
});

type Props = {
  task: CollectionToastContent_taskInterface;
  previousProgress: number;
  onClose: () => void;
};
export const CollectionToastContent = ({
  task,
  previousProgress,
  onClose,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const enableSorare27Nav = useEnableSorare27Nav();
  const [displayedLiveScore, setDisplayedLiveScore] =
    useState(previousProgress);
  const [hideInitialContent, setHideInitialContent] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const { formatMessage } = useIntl();

  const isSupportedTask = isTypes(task, [
    'CardsCountTargetTask',
    'CompleteCollectionsCountTargetTask',
  ]);

  useTimeoutFn(() => {
    if (isSupportedTask) {
      setDisplayedLiveScore(task.progress);
    }
  }, 200);

  if (!isSupportedTask) {
    return null;
  }

  const { title, iconUrl, progress, target, color, rarity, so5Competitions } =
    task;

  const isComplete = progress >= target;

  const onProgressAnimationEnd = () => {
    if (isComplete) {
      setHideInitialContent(true);
    }
  };

  const completeMessage = formatMessage(message);

  const getCollectPath = () => {
    if (rarity === Rarity.common || rarity === null) {
      return generateSportContextPath(ANY_SPORT_SERIES_MISSIONS_COLLECT);
    }

    if (enableSorare27Nav) {
      return withTargetSo5CompetitionSearchParam(
        generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO),
        so5Competitions[0]?.slug
      );
    }

    return generateSportContextPath(ANY_SPORT_MISSIONS_CATEGORY_RARITY, {
      params: {
        rarity: rarity.toLowerCase(),
        category: 'collect',
      },
    });
  };

  return (
    <Link draggable={false} to={getCollectPath()} onClick={onClose}>
      <MissionToastBackground
        color={color}
        showHalo={hideInitialContent}
        showShimmer={hideInitialContent}
      >
        <HiddenOverflow>
          <InitialContent
            className={classNames({ hide: hideInitialContent })}
            onAnimationEnd={e => {
              if (e.target === e.currentTarget && hideInitialContent) {
                setShowComplete(true);
              }
            }}
          >
            <Horizontal gap={1.5}>
              {!!iconUrl && <Icon src={iconUrl} alt="" />}
              <Vertical gap={0.5} grow>
                <LabelM bold>{title}</LabelM>
                <ProgressRow>
                  <ProgressWrapper>
                    <Progress
                      skipStartAnimation
                      value={displayedLiveScore}
                      max={target}
                      color="linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)"
                      onAnimationEnd={onProgressAnimationEnd}
                    />
                  </ProgressWrapper>
                  <Horizontal gap={0}>
                    <LabelM bold>
                      <SlotMachineAnimatedNumber value={displayedLiveScore} />
                    </LabelM>
                    <LabelM color="var(--c-nd-600)">
                      &nbsp;/&nbsp;{target}
                    </LabelM>
                  </Horizontal>
                </ProgressRow>
              </Vertical>
            </Horizontal>
          </InitialContent>
          <CompleteContent className={classNames({ show: showComplete })}>
            <StyledBrandXL uppercase bold>
              {[...completeMessage].map((letter, index) => {
                return (
                  <AnimatedLetter
                    // oxlint-disable-next-line react/no-array-index-key
                    key={index}
                    style={
                      {
                        '--offsetFactor': completeMessage.length - 1 - index,
                      } as CSSProperties
                    }
                    className={classNames({ animate: showComplete })}
                  >
                    {letter}
                  </AnimatedLetter>
                );
              })}
            </StyledBrandXL>
          </CompleteContent>
        </HiddenOverflow>
      </MissionToastBackground>
    </Link>
  );
};

CollectionToastContent.fragments = {
  taskInterface: gql`
    fragment CollectionToastContent_taskInterface on TaskInterface {
      id
      title
      iconUrl
      color
      ... on CardsCountTargetTask {
        id
        progress
        target
        rarity
        so5Competitions {
          slug
        }
      }
      ... on CompleteCollectionsCountTargetTask {
        id
        progress
        target
        rarity
        so5Competitions {
          slug
        }
      }
    }
  ` as TypedDocumentNode<CollectionToastContent_taskInterface>,
};
